import { parseSkillFrontmatter, type SkillManifest } from '../models/skillParameters';

/**
 * Skills 1.5 (PRD Option A) — best-effort capture of a skill's `SKILL.md`
 * manifest at install. A Postgres RPC can't reach GitHub, so the install path
 * fetches the raw `SKILL.md` at the pinned ref here, parses its frontmatter
 * `parameters`, and passes the result to `install_skill(p_manifest)`.
 *
 * Best-effort + NON-BLOCKING by contract: any network/parse failure (private
 * repo, missing file, rate-limit, offline) resolves to `null` — install still
 * succeeds and the skill degrades to instant-insert (no card). The first
 * candidate path that yields a parameterised manifest wins.
 */
export async function captureSkillManifest(opts: {
  owner: string;
  repo: string;
  name: string;
  ref: string | null;
}): Promise<SkillManifest | null> {
  const ref = opts.ref && opts.ref.trim() ? opts.ref.trim() : 'HEAD';
  // Common SKILL.md locations, root-first. The repo/skill layout isn't fixed, so
  // we try a small ordered set and stop at the first parameterised manifest.
  const paths = [
    'SKILL.md',
    `${opts.name}/SKILL.md`,
    `skills/${opts.name}/SKILL.md`,
    `.claude/skills/${opts.name}/SKILL.md`
  ];
  for (const p of paths) {
    try {
      const url = `https://raw.githubusercontent.com/${opts.owner}/${opts.repo}/${encodeURIComponent(
        ref
      )}/${p}`;
      const res = await fetch(url);
      if (!res.ok) continue;
      const md = await res.text();
      const manifest = parseSkillFrontmatter(md);
      if (manifest.parameters.length > 0) return manifest;
    } catch {
      // network/parse failure on this candidate → try the next, then degrade.
    }
  }
  return null;
}
